import { FC, PropsWithChildren, useLayoutEffect, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import AppLogo from '@/components/ui/AppLogo/AppLogo.tsx';
import Navbar from '@/layouts/Admin/Navbar.tsx';
import { Button, IconButton, SwipeableDrawer } from '@mui/material';
import { Menu, Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AdminLayoutProvider, useAdminLayoutContext } from '@/layouts/Admin/AdminLayoutProvider.tsx';
import Portal from '@/components/shared/Portal/Portal.tsx';
import CloseIcon from '@mui/icons-material/Close';

const Layout: FC = () => {
  return (
    <AdminLayoutProvider>
      <LayoutInner />
    </AdminLayoutProvider>
  );
};

const LayoutInner: FC = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <>
      <div className="flex grow min-h-screen flex-col lg:flex-row">
        <aside className="relative flex lg:flex-col gap-6 p-2 lg:py-6 lg:min-w-[256px] bg-primary-400">
          <div className="absolute left-0 top-0 h-full w-full nav-gradient pointer-events-none" />
          <div className="lg:px-4 flex flex-col justify-center">
            <Link to={Routes.HOME}>
              <AppLogo.White className="w-[189px]" />
            </Link>
          </div>
          <div className="flex grow flex-col justify-center items-end lg:justify-start lg:items-stretch">
            <Navbar className="hidden lg:flex" />
            <IconButton onClick={() => setIsDrawerOpen((prev) => !prev)} className="text-white p-3 lg:hidden">
              <Menu />
            </IconButton>
            <SwipeableDrawer
              anchor={'right'}
              open={isDrawerOpen}
              onClose={() => (isDrawerOpen ? setIsDrawerOpen(false) : null)}
              onOpen={() => (!isDrawerOpen ? setIsDrawerOpen(true) : null)}
            >
              <div className="flex flex-col max-w-[100vw]">
                <div className="bg-white pt-6 pb-4 px-6 sticky top-0 z-10">
                  <Button
                    variant="text"
                    color="inherit"
                    size="small"
                    startIcon={<Close />}
                    onClick={() => setIsDrawerOpen(false)}
                  >
                    {t('button.close')}
                  </Button>
                </div>
                <div className="pb-6 px-6">
                  <Navbar onClick={() => setIsDrawerOpen(false)} className="w-[250px] max-w-full" variant="dark" />
                </div>
              </div>
            </SwipeableDrawer>
          </div>
        </aside>
        <main
          id="default-layout-content-container"
          className="lg:z-10 relative flex flex-col grow gap-4 md:gap-8 lg:max-h-screen overflow-auto p-4 md:p-10 lg:p-20 lg:max-w-[calc(100%-256px)]"
        >
          <Outlet />
        </main>
      </div>
      <LayoutDrawer />
    </>
  );
};

const Drawer: FC<PropsWithChildren> = ({ children }) => {
  const [watch, setWatch] = useState(false);

  useLayoutEffect(() => {
    setWatch((prev) => !prev);
  }, []);

  return (
    <Portal watch={watch} elementId={'company-admin-layout-drawer'}>
      {children}
    </Portal>
  );
};

const LayoutDrawer: FC = () => {
  const { isDrawerOpen, setIsDrawerOpen } = useAdminLayoutContext();
  const { t } = useTranslation();

  return (
    <SwipeableDrawer
      anchor={'right'}
      open={isDrawerOpen}
      onClose={() => (isDrawerOpen ? setIsDrawerOpen(false) : null)}
      onOpen={() => (!isDrawerOpen ? setIsDrawerOpen(true) : null)}
      ModalProps={{
        container: () => document.querySelector('#default-layout-content-container'),
        keepMounted: true,
      }}
    >
      <div className="flex flex-col max-w-[100vw]">
        <div className="bg-white pt-6 pb-4 px-6 sticky top-0 z-10">
          <Button
            variant="text"
            color="inherit"
            size="small"
            startIcon={<CloseIcon />}
            onClick={() => setIsDrawerOpen(false)}
          >
            {t('button.close')}
          </Button>
        </div>
        <div id="company-admin-layout-drawer" className="empty:hidden pb-6 px-6" />
      </div>
    </SwipeableDrawer>
  );
};

export default {
  Layout,
  Drawer,
};
